/**
 * 앱의 «밝게/어둡게» 를 읽고 바뀔 때 알려 준다 (2026-09-02).
 *
 * ## 왜 필요한가
 *
 * 오피스 편집기(`packages/office`)는 호스트가 주는 `getTheme()` 으로 자기 스코프의
 * `data-theme` 을 세운다. 여기서 `'system'` 을 주면 편집기는 `data-theme` 을 **지우고**,
 * 그 결과 편집기 CSS 의
 *
 *   `@media (prefers-color-scheme: dark) { .go-root:not([data-theme=light]):not([data-theme=dark]) }`
 *
 * 가 걸린다 — 즉 편집기가 **OS 설정**을 본다. 앱의 모드와 아무 상관이 없다.
 *
 * 그래서 앱이 밝게인데 OS 가 어둡게인 사람은 **흰 앱 안에서 편집기만 새까맣게** 떴다
 * (2026-09-02 운영 실측: 앱 배경 밝기 244 vs 편집기 배경 밝기 37). 반대도 마찬가지다.
 *
 * ## 어디서 읽는가
 *
 * `themeStore` 가 `document.documentElement` 의 `data-mode` 에 **resolve 된 값만**
 * (`light` | `dark`) 적는다 — `auto` 는 절대 안 들어간다(`store/themeStore.ts` 참조).
 * 그래서 이 속성 하나만 보면 되고, `auto` 처리는 이미 끝나 있다.
 *
 * 구독은 zustand 가 아니라 **속성 관찰**로 한다. `data-mode` 를 적는 경로가 스토어
 * 하나라는 보장이 없고(로그인 화면·스토리북도 직접 적는다), 속성을 보면 어느 경로로
 * 바뀌든 똑같이 잡힌다.
 */

export type AppColorMode = 'light' | 'dark';

/** 지금 화면이 어두운가 — `data-mode` 에는 resolve 된 light|dark 만 들어 있다. */
export function currentColorMode(): AppColorMode {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-mode') === 'dark' ? 'dark' : 'light';
}

/**
 * 모드가 **실제로 바뀔 때만** 부른다 — `data-mode` 를 같은 값으로 다시 적는 경로가 있어서
 * (테마 색만 바꿔도 `applyToDocument` 가 둘 다 적는다) 그대로 흘리면 편집기가 쓸데없이
 * 다시 그려진다.
 *
 * @returns 구독 해지 함수
 */
export function subscribeColorMode(fn: (mode: AppColorMode) => void): () => void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
  let last = currentColorMode();
  const obs = new MutationObserver(() => {
    const now = currentColorMode();
    if (now === last) return;
    last = now;
    fn(now);
  });
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mode'] });
  return () => obs.disconnect();
}
